import { useState, useEffect } from 'react';
import {
    fetchAllRoles,
    type RoleRecord,
} from '../../services/staffApi';
import './Designations.css';

/* ── Component ─────────────────────────────────────── */

export default function Designations() {
    /* --- data state --- */
    const [roles, setRoles] = useState<RoleRecord[]>([]);

    /* --- view state --- */
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');

    /* --- load on mount --- */
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                setFetchError('');
                const data = await fetchAllRoles();
                setRoles(data);
            } catch (err: unknown) {
                const message = err && typeof err === 'object' && 'message' in err
                    ? (err as { message: string }).message
                    : 'Failed to load roles.';
                setFetchError(message);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    /* ────────────────── Render: List View ────────────────── */
    return (
        <div className="desig">
            {successMsg && (
                <div className="desig__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>{successMsg}</span>
                    <button className="desig__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="desig__header">
                <div className="desig__header-text">
                    <h1 className="desig__title">Roles</h1>
                    <p className="desig__subtitle">View all roles and their permissions in your organisation.</p>
                </div>
            </div>

            {fetchError && (
                <div className="desig__error-banner">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {fetchError}
                </div>
            )}

            <div className="desig__table-wrap">
                {isLoading ? (
                    <div className="desig__loading">
                        <svg className="desig__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" strokeWidth="3" />
                        </svg>
                        <span>Loading…</span>
                    </div>
                ) : roles.length === 0 ? (
                    <div className="desig__empty">
                        <div className="desig__empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="8" r="4" />
                                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <h3 className="desig__empty-title">No roles found</h3>
                        <p className="desig__empty-text">No roles have been configured yet.</p>
                    </div>
                ) : (
                    <table className="desig__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Permissions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map((r) => (
                                <tr key={r.id}>
                                    <td className="desig__col-name">{r.name}</td>
                                    <td>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {r.permissions.map((p) => (
                                                <span
                                                    key={p}
                                                    className="desig__badge desig__badge--active"
                                                    style={{ fontSize: '0.72rem', textTransform: 'none', letterSpacing: 0 }}
                                                >
                                                    {p.replace(/_/g, ' ')}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
